import type { VaultSidebarMenuItem } from './vault-sidebar-menu.types';

/**
 * Estructura lateral Vault (referencia UI superadmin / producción).
 * Grupos Simappe → hijos en `children` (optionGroups → options).
 *
 * @generated 2026-05-29 | re-exportar desde read-by-roles-grouped superadmin si Simappe cambia
 */
export const VAULT_MENU_CANONICAL: VaultSidebarMenuItem[] = [
  {
    label: 'Mi Tablero',
    icon: 'House',
    children: [
      {
        label: 'Inicio',
        icon: 'Gauge',
        route: '/layout/vault/dashboard',
        children: [],
      },
    ],
    expanded: true,
  },
  {
    label: 'Centro Documental',
    icon: 'Folder',
    children: [
      {
        label: 'Carga de Documentos',
        icon: 'FolderUp',
        route: '/layout/vault/vault/projects',
        children: [],
      },
      // 'Preservación Digital' oculto temporalmente (desarrollos futuros). Ruta conservada
      // en vault.routes.ts para reactivar sin re-crear el módulo.
      {
        label: 'Retención Documental',
        icon: 'FolderGit2',
        route: '/layout/vault/retention',
        children: [],
      },
      {
        label: 'Custodia Legal',
        icon: 'FolderArchive',
        route: '/layout/vault/legal-hold',
        children: [],
      },
      {
        label: 'Bóveda Digital',
        icon: 'Vault',
        route: '/layout/vault/repository-admin',
        children: [],
      },
      {
        label: 'Reportes Generales',
        icon: 'BarChart3',
        route: '/layout/vault/reports',
        children: [],
      },
      {
        label: 'Respaldo Externo',
        icon: 'FolderSync',
        route: '/layout/vault/cloud',
        children: [],
      },
    ],
    expanded: true,
  },
  {
    label: 'Centro de control',
    icon: 'BetweenHorizontalEnd',
    children: [
      {
        label: 'Auditoría',
        icon: 'SearchCheck',
        route: '/layout/vault/audit',
        children: [],
      },
      {
        label: 'Configuración',
        icon: 'Settings',
        route: '/layout/vault/settings',
        children: [],
      },
      {
        label: 'Mensajes Informativos',
        icon: 'MessageSquareDot',
        route: '/layout/vault/preservation-messages',
        children: [],
      },
    ],
    expanded: true,
  },
  {
    label: 'Administrador',
    icon: 'UserStar',
    children: [
      {
        label: 'Gestión de Empresas',
        icon: 'Building2',
        route: '/layout/vault/admin/tenants',
        children: [],
      },
      {
        label: 'Gestión de Usuarios',
        icon: 'Users',
        route: '/layout/vault/admin/user',
        children: [],
      },
    ],
    expanded: true,
  },
];

export function countCanonicalVaultLeafRoutes(): number {
  return flattenVaultMenuRouteKeys(VAULT_MENU_CANONICAL).size;
}

export function flattenVaultMenuRouteKeys(items: VaultSidebarMenuItem[]): Set<string> {
  const keys = new Set<string>();
  const walk = (list: VaultSidebarMenuItem[]) => {
    for (const item of list) {
      if (item.children?.length) {
        walk(item.children);
      } else if (item.route) {
        const key = normalizeVaultMenuRouteKey(item.route);
        if (key) keys.add(key);
      }
    }
  };
  walk(items);
  return keys;
}

const VAULT_LAYOUT_LEAF_SEGMENTS = new Set([
  'dashboard',
  'preservation',
  'retention',
  'legal-hold',
  'reports',
  'cloud',
  'notifications',
  'audit',
  'settings',
  'preservation-messages',
  'repository-admin',
  'admin',
  'vault',
  'access-denied',
]);

/** Corrige rutas Vault mal prefijadas (p. ej. `/layout/accounting/retention`). */
function coerceVaultLayoutRouteKey(normalized: string): string {
  if (normalized.includes('/layout/vault/')) return normalized;

  const pathOnly = normalized.includes('://')
    ? (() => {
        try {
          return new URL(normalized).pathname.toLowerCase();
        } catch {
          return normalized;
        }
      })()
    : normalized;

  const parts = pathOnly.split('/').filter(Boolean);
  if (parts[0] !== 'layout' || parts.length < 2) return normalized;

  const layoutModule = parts[1];
  if (layoutModule === 'vault') return pathOnly;

  if (parts[2] === 'vault') {
    return `/layout/vault/${parts.slice(3).join('/')}`.replace(/\/$/, '') || '/layout/vault';
  }

  if (parts.length >= 3 && VAULT_LAYOUT_LEAF_SEGMENTS.has(parts[2])) {
    return `/layout/vault/${parts.slice(2).join('/')}`.replace(/\/$/, '') || '/layout/vault';
  }

  return pathOnly;
}

export function normalizeVaultMenuRouteKey(route: string | undefined | null): string {
  if (!route || typeof route !== 'string') return '';
  let normalized = route.trim().toLowerCase();
  normalized = normalized.split('?')[0].split('#')[0];
  normalized = normalized.replace(/\/+/g, '/');
  normalized = normalized.replace(/tesoreria/g, 'tesorería');
  normalized = normalized.replace(/\/admin\/users(\/|$)/gi, '/admin/user$1');
  if (!normalized.startsWith('/')) normalized = `/${normalized}`;
  normalized = coerceVaultLayoutRouteKey(normalized);
  if (normalized.includes('/layout/vault/')) return normalized;
  if (normalized.startsWith('/vault/')) return `/layout${normalized}`;
  if (normalized.startsWith('/admin/')) return `/layout/vault${normalized}`;
  return normalized;
}
